import menuStore from './localForageInstance';
import { callAPI } from './apiUtils';
import { apiUrls } from './apiUrls';
import { toast } from 'react-toastify';

// Module-level (not React state) so it survives the exact moment a route change unmounts one
// page's <Header> and mounts the next one's - a plain window event fired right at that boundary
// can be missed by both instances (the old one already removed its listener, the new one hasn't
// attached its yet). Header.js reads this synchronously on mount to self-correct regardless of
// whether it caught the start event.
let syncing = false;
export function isMenuSyncing() {
  return syncing;
}

/**
 * Refreshes the local menu cache (category/dish tree + per-product detail, all read from
 * IndexedDB by the ordering screens) from the live API. Shared by the Header's manual "Sync
 * Menu" button and the Product Management page's auto-sync-on-navigate-away, so both stay
 * behaviorally identical instead of drifting into two copies of the same fetch+cache logic.
 *
 * Dispatches `menuSyncStarted`/`menuSyncFinished` on window rather than taking a loading-state
 * callback, so Header.js's existing full-page "Syncing Menu..." overlay (previously wired only
 * to its own button click) picks this up automatically no matter which caller kicked off the
 * sync - the caller doesn't need to know Header.js's overlay exists at all.
 *
 * Reloads the page 2s after a successful sync by default - the ordering screens only read this
 * cache once on mount, so without a reload the fresh data would sit in IndexedDB unseen until
 * some unrelated future reload. Pass { reloadAfter: false } for a sync where a forced reload
 * would be disruptive.
 */
export async function syncMenu({ reloadAfter = true, showToast = true } = {}) {
  syncing = true;
  window.dispatchEvent(new Event('menuSyncStarted'));
  try {
    const [categoriesRes, dishesRes] = await Promise.all([
      callAPI(apiUrls.CATEGORY_LISTING, 'GET'),
      callAPI(apiUrls.MENU_DISHES, 'GET'),
    ]);
    const dishCategoryItems = categoriesRes.data?.data || [];
    const dishesData = dishesRes.data?.data || [];
    const lastSynced = new Date().toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    await menuStore.setItem('dish_category_items', dishCategoryItems);
    await menuStore.setItem('last_synced_pos_data', lastSynced);
    await menuStore.setItem('dish_menu_items', true);

    for (const product of dishesData) {
      if (product?.product_key?.startsWith('product_')) {
        await menuStore.setItem(product.product_key, product);
      }
    }

    if (showToast) {
      toast.success('Menu data synced successfully!');
    }

    if (reloadAfter) {
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  } catch (err) {
    if (showToast) {
      toast.error('Error syncing menu data.');
    }
    throw err;
  } finally {
    syncing = false;
    window.dispatchEvent(new Event('menuSyncFinished'));
  }
}
